import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, ShieldCheck } from 'lucide-react';
import { adminSsoAPI } from '@/lib/api';

/**
 * Cross-origin handoff target for eva-website's admin panel (EIP-330).
 *
 * The panel can't run the passkey step-up ceremony on its own origin -- the
 * credential's RP ID is pinned to this app's origin -- so it sends the
 * superadmin here with a top-level navigation instead of a popup. This page
 * is deliberately NOT wrapped in PublicRoute or ProtectedRoute: both redirect
 * on a plain <Navigate> and drop the query string, which would silently
 * discard return_url/state on the exact request this page exists to carry.
 * It does its own auth branching instead, mirroring SSOPage.js.
 *
 * A code is minted only on an explicit click, never on mount -- minting on
 * mount would make this a zero-interaction, cross-site-triggerable
 * credential grant (any page could navigate here and get one for free).
 */
export default function AdminSsoAuthorizePage() {
  const { user, loading, isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState('');

  const returnUrl = searchParams.get('return_url') || '';
  const state = searchParams.get('state') || '';

  let destinationHost = null;
  try {
    destinationHost = returnUrl ? new URL(returnUrl).host : null;
  } catch {
    destinationHost = null;
  }

  useEffect(() => {
    if (loading || user) return;
    const here = window.location.pathname + window.location.search;
    navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
  }, [loading, user, navigate]);

  const handleContinue = async () => {
    setIssuing(true);
    setError('');
    try {
      const { redirect_url: redirectUrl } = await adminSsoAPI.issueCode(returnUrl, state);
      window.location.replace(redirectUrl);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Could not continue to the admin panel. Please try again.'
      );
      setIssuing(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-sm font-medium text-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!destinationHost) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm border-border shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <p className="font-semibold text-foreground">Invalid request</p>
            <p className="mt-2 text-sm text-muted-foreground">
              This sign-in link is missing or has an invalid destination.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm border-border shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <p className="font-semibold text-foreground">Not available</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Only Eva Insights superadmins can sign in to the admin panel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm border-border shadow-xl" data-testid="admin-sso-authorize-panel">
        <CardHeader className="space-y-1 pb-4 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl font-bold text-foreground">Continue to admin panel</CardTitle>
          <CardDescription className="text-muted-foreground">
            Signed in as {user.email}. Continue to{' '}
            <span className="font-medium text-foreground">{destinationHost}</span>?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-xl border border-destructive/20">
              {error}
            </div>
          )}
          <Button
            type="button"
            className="w-full h-11"
            onClick={handleContinue}
            disabled={issuing}
            data-testid="admin-sso-continue"
          >
            {issuing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Continuing...
              </span>
            ) : (
              'Continue'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
